import { formatDate, formatDateIST, formatDateUTC } from "@/utils/date";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { ArrowUpDownIcon, MoreHorizontal, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage } from "../../common/Error";
import { ShimmerTable } from "../../common/Table";
import Text from "../../common/Text";
import { ScheduledJob } from "@/types/common.types";
import { cn } from "@/lib/utils";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Button } from "../../ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { CustomAlert } from "../../ui/Alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VirtualTable } from "@/components/common/VirtualTable";
import { COLOR_MAP, PAGE_SIZE, STATUS_DISPLAY_MAP } from "@/lib/constants";
import { useGetRolesQuery, useGetScheduledJobsQuery } from "@/hooks/queries";
import { useDeleteJobsMutation } from "@/hooks/mutations";
import { useGetTenantsQuery } from "@/hooks/queries";
import { useReassignTenantsMutation } from "@/hooks/mutations/useReassignTenantsMutation";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

type JobsListTableProps = {
  onJobClick: (job: ScheduledJob) => void;
  search: string;
  setSearch: (search: string) => void;
  debouncedSearchValue: string;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  setColumnOptions: (options: Array<{ id: string; label: string }>) => void;
  setInitialSelectedColumns: (columns: string[]) => void;
};

const JOB_LIST_COLUMNS_LABELS = {
  jobName: "Dataset",
  totalStyleIds: "Total Style Ids",
  tenants: "Tenants",
  label: "Status",
  startTime: "Start Date",
  endTime: "End Date",
  frequency: "Frequency",
  lastExecutionTime: "Last Fetched Time",
  actions: "Action",
};
const Badge = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      "inline-block rounded-none px-2 py-1 text-xs leading-[14px]",
      className
    )}
  >
    {children}
  </span>
);
const Pill = ({ status }: { status: keyof typeof COLOR_MAP }) => {
  return (
    <Badge
      className={cn(
        "items-center justify-start rounded-none px-2 py-1 text-xs font-normal",
        COLOR_MAP[status]
      )}
    >
      {STATUS_DISPLAY_MAP[status]}
    </Badge>
  );
};
const ActionsCell = ({
  row,
  onDeleteClick,
  isSuperAdmin,
}: {
  row: { original: ScheduledJob };
  onDeleteClick: (job: ScheduledJob) => void;
  isSuperAdmin: boolean;
}) => {
  const [showActions, setShowActions] = useState(false);
  const { mutate: reassignTenants } = useReassignTenantsMutation();
  const queryClient = useQueryClient();
  const [selectedTenants, setSelectedTenants] = useState<string[]>(
    row.original.tenants?.map((t: { id: number }) => t.id.toString()) || []
  );

  const handleTenantsChange = (newTenants: string[]) => {
    setSelectedTenants(newTenants);
    reassignTenants(
      {
        jobId: row.original.id.toString(),
        tenantIds: newTenants,
      },
      {
        onSuccess: () => {
          CustomAlert.success({
            description: "Tenants reassigned successfully",
          });
          queryClient.invalidateQueries({
            queryKey: ["scheduledJobs"],
          });
        },
        onError: () => {
          CustomAlert.error({
            description: "Failed to reassign tenants",
          });
        },
      }
    );
  };

  return (
    <Popover open={showActions} onOpenChange={setShowActions}>
      <PopoverTrigger asChild>
        <div
          className="flex cursor-pointer items-center justify-start"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4 text-gray-500 hover:text-gray-700" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        {isSuperAdmin && (
          <TenantSelector
            selectedTenants={selectedTenants}
            onTenantsChange={handleTenantsChange}
            onClose={() => setShowActions(false)}
            onDelete={() => {
              onDeleteClick(row.original);
              setShowActions(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
const TenantSelector = ({
  selectedTenants,
  onTenantsChange,
  onClose,
  onDelete,
}: {
  selectedTenants: string[];
  onTenantsChange: (tenants: string[]) => void;
  onClose: () => void;
  onDelete: () => void;
}) => {
  const { data: tenants } = useGetTenantsQuery();
  const [tempSelectedTenants, setTempSelectedTenants] =
    useState<string[]>(selectedTenants);

  const toggleTenant = (tenantId: string) => {
    setTempSelectedTenants(
      tempSelectedTenants.includes(tenantId)
        ? tempSelectedTenants.filter((id) => id !== tenantId)
        : [...tempSelectedTenants, tenantId]
    );
  };

  const handleSave = () => {
    onTenantsChange(tempSelectedTenants);
    onClose();
  };

  return (
    <div className="p-4">
      <div className="mb-4 text-sm font-medium">Select Tenants</div>
      <motion.div className="flex flex-wrap gap-2">
        {tenants?.map((tenant) => {
          const isSelected = tempSelectedTenants.includes(tenant.id.toString());
          return (
            <motion.button
              key={tenant.id}
              onClick={() => toggleTenant(tenant.id.toString())}
              className={cn(
                "inline-flex items-center rounded-none px-3 py-1 text-sm text-zinc-400 ring-1 ring-inset ring-shades-neutral-200",
                isSelected &&
                  "bg-brand-accent-soft text-brand-accent ring-brand-accent"
              )}
            >
              <span>{tenant.name}</span>
              {isSelected && (
                <Check className="ml-1 h-3 w-3 text-brand-accent" />
              )}
            </motion.button>
          );
        })}
      </motion.div>
      <div className="mt-4 flex w-full justify-end gap-2">
        <Button
          variant="ghost"
          className="w-fit items-center justify-start rounded-none bg-red-600 text-white hover:bg-red-700"
          onClick={onDelete}
        >
          Delete Job
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>

        <Button onClick={handleSave} className="text-white">
          Save
        </Button>
      </div>
    </div>
  );
};
const JobsListTable: React.FC<JobsListTableProps> = ({
  onJobClick,
  search,
  setSearch,
  debouncedSearchValue,
  columnVisibility,
  setColumnOptions,
  setInitialSelectedColumns,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetScheduledJobsQuery(PAGE_SIZE, debouncedSearchValue);
  const { data: rolesData } = useGetRolesQuery();
  const { sendEvent } = useAnalyticsManager();
  const [jobToDelete, setJobToDelete] = useState<ScheduledJob | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (job: ScheduledJob) => {
    setJobToDelete(job);
  };

  const { mutate: deleteJob } = useDeleteJobsMutation();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (debouncedSearchValue) {
      sendEvent("job_search_initiated", {
        search_query: debouncedSearchValue,
        screen_name: "jobs list table",
      });
    }
  }, [debouncedSearchValue]);
  const handleConfirmDelete = () => {
    if (jobToDelete) {
      setIsDeleting(true);
      deleteJob(jobToDelete.id, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["scheduledJobs"],
          });
          CustomAlert.success({
            description: "Job deleted successfully",
          });
          setIsDeleting(false);
          setJobToDelete(null);
        },
        onError: () => {
          CustomAlert.error({
            description: "Failed to delete job",
          });
          setJobToDelete(null);
        },
      });
    }
  };

  const jobListColumns: ColumnDef<ScheduledJob>[] = useMemo(
    () =>
      rolesData?.isSuperAdmin
        ? [
            {
              accessorKey: "jobName",
              header: "Dataset",
              cell: ({ row }) => {
                return (
                  <div
                    className="cursor-pointer text-left capitalize text-brand-accent underline"
                    onClick={() => onJobClick(row.original)}
                  >
                    {row.original.jobName}
                  </div>
                );
              },
            },
            {
              accessorKey: "tenants",
              header: "Tenants",
              cell: ({ row }) => (
                <div className="text-left">
                  {row.original?.tenants?.length > 0 ? (
                    <div className="flex flex-wrap justify-start gap-1">
                      {row.original.tenants.map((tenant) => (
                        <Badge
                          key={tenant.id}
                          className="bg-shades-neutral-50 text-shades-neutral-400"
                        >
                          {tenant.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <Badge className="bg-shades-neutral-50 text-shades-neutral-400">
                      No Tenants
                    </Badge>
                  )}
                </div>
              ),
            },
            {
              accessorKey: "totalStyleIds",
              header: "Total Style Ids",
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {row.original.totalStyleIds}
                </div>
              ),
            },
            {
              accessorKey: "label",
              header: "Status",
              cell: ({ row }) => (
                <div className="flex justify-start">
                  <Pill
                    status={
                      row.original.scheduledJobStatus as
                        | "SUCCESS"
                        | "FAILURE"
                        | "INPROGRESS"
                    }
                  />
                </div>
              ),
            },
            {
              accessorKey: "startTime",
              header: ({ column }) => (
                <div
                  className="flex cursor-pointer items-center justify-start gap-2"
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                >
                  <ArrowUpDownIcon className="ml-2 h-4 w-4" />
                  Start Date
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {formatDate(row.original.startTime)}
                </div>
              ),
            },
            {
              accessorKey: "endTime",
              header: ({ column }) => (
                <div
                  className="flex cursor-pointer items-center justify-start gap-2"
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                >
                  <ArrowUpDownIcon className="ml-2 h-4 w-4" />
                  End Date
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {formatDate(row.original.endTime)}
                </div>
              ),
            },
            {
              accessorKey: "frequency",
              header: "Frequency",
              cell: ({ row }) => {
                return (
                  <div className="text-left capitalize">
                    {row.original.frequency}
                  </div>
                );
              },
            },
            {
              accessorKey: "lastExecutionTime",
              header: "Last Fetched Time",
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {formatDateIST(row.original.lastExecutionTime)}
                </div>
              ),
            },
            {
              id: "actions",
              header: "Action",
              cell: ({ row }) => (
                <ActionsCell
                  row={row}
                  onDeleteClick={handleDeleteClick}
                  isSuperAdmin={rolesData?.isSuperAdmin}
                />
              ),
            },
          ]
        : [
            {
              accessorKey: "jobName",
              header: "Dataset",
              cell: ({ row }) => {
                return (
                  <div
                    className="cursor-pointer text-left capitalize text-brand-accent underline"
                    onClick={() => onJobClick(row.original)}
                  >
                    {row.original.jobName}
                  </div>
                );
              },
            },
            {
              accessorKey: "totalStyleIds",
              header: "Total Style Ids",
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {row.original.totalStyleIds}
                </div>
              ),
            },
            {
              accessorKey: "label",
              header: "Status",
              cell: ({ row }) => (
                <div className="flex justify-start">
                  <Pill
                    status={
                      row.original.scheduledJobStatus as
                        | "SUCCESS"
                        | "FAILURE"
                        | "INPROGRESS"
                    }
                  />
                </div>
              ),
            },
            {
              accessorKey: "startTime",
              header: ({ column }) => (
                <div
                  className="flex cursor-pointer items-center justify-start gap-2"
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                >
                  <ArrowUpDownIcon className="ml-2 h-4 w-4" />
                  Start Date
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {formatDate(row.original.startTime)}
                </div>
              ),
            },
            {
              accessorKey: "endTime",
              header: ({ column }) => (
                <div
                  className="flex cursor-pointer items-center justify-start gap-2"
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                >
                  <ArrowUpDownIcon className="ml-2 h-4 w-4" />
                  End Date
                </div>
              ),
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {formatDate(row.original.endTime)}
                </div>
              ),
            },
            {
              accessorKey: "frequency",
              header: "Frequency",
              cell: ({ row }) => {
                return (
                  <div className="text-left capitalize">
                    {row.original.frequency}
                  </div>
                );
              },
            },
            {
              accessorKey: "lastExecutionTime",
              header: "Last Fetched Time",
              cell: ({ row }) => (
                <div className="text-left capitalize">
                  {formatDateIST(row.original.lastExecutionTime)}
                </div>
              ),
            },
            {
              id: "actions",
              header: "Action",
              cell: ({ row }) => {
                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <div
                        className="flex cursor-pointer items-center justify-start"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-30 rounded-none p-0"
                      align="end"
                    >
                      <Button
                        variant="ghost"
                        className="w-full items-center justify-start rounded-none bg-red-600 text-white hover:bg-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(row.original);
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
                    </PopoverContent>
                  </Popover>
                );
              },
            },
          ],
    [onJobClick]
  );
  const tableData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data?.pages]);

  const table = useReactTable({
    data: tableData,
    columns: jobListColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility,
    },
  });

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (table) {
      const options = table
        .getAllColumns()
        .filter((column) => column.getCanHide())
        .map((column) => ({
          id: column.id,
          label:
            JOB_LIST_COLUMNS_LABELS[
              column.id as keyof typeof JOB_LIST_COLUMNS_LABELS
            ],
        }));
      setColumnOptions(options);

      const selected = table
        .getAllColumns()
        .filter((column) => column.getIsVisible())
        .map((column) => column.id);
      setInitialSelectedColumns(selected);
    }
  }, [table, setColumnOptions, setInitialSelectedColumns]);

  if (isLoading) {
    return (
      <JobListHeader search={search} setSearch={setSearch} totalJobs={0}>
        <ShimmerTable<ScheduledJob> columns={jobListColumns} rowCount={5} />
      </JobListHeader>
    );
  }

  if (isError) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <>
      <JobListHeader
        search={search}
        setSearch={setSearch}
        totalJobs={data?.pages?.[0]?.totalElements || 0}
      >
        <VirtualTable
          table={table}
          loadMore={loadMore}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          columns={jobListColumns}
        />
      </JobListHeader>

      <AlertDialog
        open={!!jobToDelete}
        onOpenChange={() => isDeleting && setJobToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDeleting ? "Hang on tight" : "Delete Job"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isDeleting
                ? "We're deleting your job. This may take a moment..."
                : "Are you sure you want to delete this job? This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeleting}
              className="disabled:hidden"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting the job" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default JobsListTable;

type JobListHeaderProps = {
  search: string;
  setSearch: (search: string) => void;
  totalJobs: number;
  children: React.ReactNode;
};
const JobListHeader: React.FC<JobListHeaderProps> = ({
  children,
  totalJobs,
}) => {
  return (
    <>
      {totalJobs > 0 ? (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing {totalJobs} Dataset
          </Text>
        </p>
      ) : (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing 0 Dataset
          </Text>
        </p>
      )}
      {children}
    </>
  );
};
